// utils/performanceMonitor.js

/**
 * Performance monitoring and optimization utilities
 */
export class PerformanceMonitor {
  static metrics = new Map();
  static observers = new Map();
  static listeners = new Set();
  static isEnabled = process.env.NODE_ENV === 'development' || 
                    localStorage.getItem('enablePerformanceMonitoring') === 'true';

  /**
   * Start performance monitoring
   */
  static init() {
    if (!this.isEnabled) return;

    console.log('Initializing performance monitoring...');

    // Monitor Core Web Vitals
    this.initWebVitals();
    
    // Monitor memory usage
    this.initMemoryMonitoring();
    
    // Monitor network performance
    this.initNetworkMonitoring();
    
    // Monitor React rendering
    this.initRenderMonitoring();

    console.log('Performance monitoring initialized');
  }

  /**
   * Initialize Core Web Vitals monitoring
   */
  static initWebVitals() {
    // Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        this.recordMetric('LCP', lastEntry.startTime, {
          element: lastEntry.element?.tagName,
          url: lastEntry.url
        });
      });
      
      try {
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        this.observers.set('lcp', lcpObserver);
      } catch (error) {
        console.warn('LCP monitoring not supported:', error);
      }

      // First Input Delay (FID)
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          this.recordMetric('FID', entry.processingStart - entry.startTime, {
            eventType: entry.name
          });
        });
      });

      try {
        fidObserver.observe({ entryTypes: ['first-input'] });
        this.observers.set('fid', fidObserver);
      } catch (error) {
        console.warn('FID monitoring not supported:', error);
      }

      // Cumulative Layout Shift (CLS)
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
            this.recordMetric('CLS', clsValue);
          }
        });
      });

      try {
        clsObserver.observe({ entryTypes: ['layout-shift'] });
        this.observers.set('cls', clsObserver);
      } catch (error) {
        console.warn('CLS monitoring not supported:', error);
      }
    }
  }

  /**
   * Initialize memory monitoring
   */
  static initMemoryMonitoring() {
    if ('memory' in performance) {
      setInterval(() => {
        const memory = performance.memory;
        this.recordMetric('memoryUsage', {
          used: memory.usedJSHeapSize,
          total: memory.totalJSHeapSize,
          limit: memory.jsHeapSizeLimit,
          percentage: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100
        });
      }, 10000); // Every 10 seconds
    }
  }

  /**
   * Initialize network monitoring
   */
  static initNetworkMonitoring() {
    if ('connection' in navigator) {
      const updateConnectionInfo = () => {
        const connection = navigator.connection;
        this.recordMetric('networkInfo', {
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt,
          saveData: connection.saveData
        });
      };

      updateConnectionInfo();
      navigator.connection.addEventListener('change', updateConnectionInfo);
    }

    // Monitor resource loading
    if ('PerformanceObserver' in window) {
      const resourceObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          if (entry.initiatorType === 'fetch' || entry.initiatorType === 'xmlhttprequest') {
            this.recordMetric('networkRequest', {
              url: entry.name,
              duration: entry.duration,
              transferSize: entry.transferSize,
              encodedBodySize: entry.encodedBodySize,
              decodedBodySize: entry.decodedBodySize
            });
          }
        });
      });

      try {
        resourceObserver.observe({ entryTypes: ['resource'] });
        this.observers.set('resource', resourceObserver);
      } catch (error) {
        console.warn('Resource monitoring not supported:', error);
      }
    }
  }

  /**
   * Initialize React rendering monitoring
   */
  static initRenderMonitoring() {
    // This would integrate with React DevTools Profiler in a real implementation
    // For now, we'll track component mount times
    this.componentMountTimes = new Map();
  }

  /**
   * Record a performance metric
   */
  static recordMetric(name, value, metadata = {}) {
    if (!this.isEnabled) return;

    const metric = {
      name,
      value,
      timestamp: Date.now(),
      metadata,
      id: `${name}_${Date.now()}_${Math.random()}`
    };

    // Store metric
    if (!this.metrics.has(name)) {
      this.metrics.set(name, []);
    }
    
    const metricArray = this.metrics.get(name);
    metricArray.push(metric);

    // Keep only last 100 entries per metric
    if (metricArray.length > 100) {
      metricArray.shift();
    }

    // Notify listeners
    this.notifyListeners(metric);

    // Log significant metrics
    this.logSignificantMetrics(metric);
  }

  /**
   * Log significant performance issues
   */
  static logSignificantMetrics(metric) {
    const { name, value } = metric;

    switch (name) {
      case 'LCP':
        if (value > 2500) {
          console.warn(`Poor LCP: ${value}ms (should be < 2500ms)`);
        }
        break;
      
      case 'FID':
        if (value > 100) {
          console.warn(`Poor FID: ${value}ms (should be < 100ms)`);
        }
        break;
      
      case 'CLS':
        if (value > 0.1) {
          console.warn(`Poor CLS: ${value} (should be < 0.1)`);
        }
        break;
      
      case 'memoryUsage':
        if (value.percentage > 80) {
          console.warn(`High memory usage: ${value.percentage.toFixed(1)}%`);
        }
        break;
    }
  }

  /**
   * Measure function execution time
   */
  static measure(name, fn) {
    if (!this.isEnabled) return fn();

    const start = performance.now();
    const result = fn();
    const end = performance.now();
    
    this.recordMetric(`function_${name}`, end - start);
    
    return result;
  }

  /**
   * Measure async function execution time
   */
  static async measureAsync(name, fn) {
    if (!this.isEnabled) return await fn();

    const start = performance.now();
    const result = await fn();
    const end = performance.now();
    
    this.recordMetric(`async_${name}`, end - start);
    
    return result;
  }

  /**
   * Mark component mount time
   */
  static markComponentMount(componentName) {
    if (!this.isEnabled) return;
    
    this.componentMountTimes.set(componentName, performance.now());
  }

  /**
   * Measure component mount duration
   */
  static measureComponentMount(componentName) {
    if (!this.isEnabled) return;
    
    const startTime = this.componentMountTimes.get(componentName);
    if (startTime) {
      const duration = performance.now() - startTime;
      this.recordMetric('componentMount', duration, { component: componentName });
      this.componentMountTimes.delete(componentName);
    }
  }

  /**
   * Get performance summary
   */
  static getSummary() {
    const summary = {
      timestamp: Date.now(),
      metrics: {},
      webVitals: {},
      recommendations: []
    };

    // Aggregate metrics
    this.metrics.forEach((values, name) => {
      if (values.length === 0) return;

      const numericValues = values
        .map(v => typeof v.value === 'number' ? v.value : null)
        .filter(v => v !== null);

      if (numericValues.length > 0) {
        summary.metrics[name] = {
          count: values.length,
          latest: values[values.length - 1].value,
          average: numericValues.reduce((a, b) => a + b, 0) / numericValues.length,
          min: Math.min(...numericValues),
          max: Math.max(...numericValues)
        };
      }
    });

    // Extract Web Vitals
    ['LCP', 'FID', 'CLS'].forEach(vital => {
      if (summary.metrics[vital]) {
        summary.webVitals[vital] = summary.metrics[vital].latest;
      }
    });

    // Generate recommendations
    summary.recommendations = this.generateRecommendations(summary);

    return summary;
  }

  /**
   * Generate performance recommendations
   */
  static generateRecommendations(summary) {
    const recommendations = [];

    // LCP recommendations
    if (summary.webVitals.LCP > 2500) {
      recommendations.push({
        type: 'LCP',
        severity: 'high',
        message: 'Largest Contentful Paint is slow. Consider optimizing images and reducing server response times.',
        value: summary.webVitals.LCP
      });
    }

    // FID recommendations
    if (summary.webVitals.FID > 100) {
      recommendations.push({
        type: 'FID',
        severity: 'medium',
        message: 'First Input Delay is high. Consider reducing JavaScript execution time.',
        value: summary.webVitals.FID
      });
    }

    // CLS recommendations
    if (summary.webVitals.CLS > 0.1) {
      recommendations.push({
        type: 'CLS',
        severity: 'medium',
        message: 'Cumulative Layout Shift is high. Ensure images have dimensions and avoid inserting content above existing content.',
        value: summary.webVitals.CLS
      });
    }

    // Memory recommendations
    if (summary.metrics.memoryUsage && summary.metrics.memoryUsage.latest.percentage > 80) {
      recommendations.push({
        type: 'memory',
        severity: 'high',
        message: 'Memory usage is high. Consider implementing virtual scrolling or pagination.',
        value: summary.metrics.memoryUsage.latest.percentage
      });
    }

    return recommendations;
  }

  /**
   * Add performance listener
   */
  static addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify listeners
   */
  static notifyListeners(metric) {
    this.listeners.forEach(callback => {
      try {
        callback(metric);
      } catch (error) {
        console.warn('Error in performance listener:', error);
      }
    });
  }

  /**
   * Export performance data
   */
  static exportData() {
    return {
      summary: this.getSummary(),
      rawMetrics: Object.fromEntries(this.metrics),
      timestamp: Date.now()
    };
  }

  /**
   * Clear all metrics
   */
  static clear() {
    this.metrics.clear();
    this.componentMountTimes?.clear();
  }

  /**
   * Cleanup observers
   */
  static cleanup() {
    this.observers.forEach(observer => {
      try {
        observer.disconnect();
      } catch (error) {
        console.warn('Error disconnecting observer:', error);
      }
    });
    this.observers.clear();
  }
}

// Initialize on module load
if (typeof window !== 'undefined') {
  PerformanceMonitor.init();
}

export default PerformanceMonitor;